import * as React from "react";
import { TaskListSquareLtr20Regular } from "@fluentui/react-icons";
import { OpenWindowConfig, useWindowManager } from "../windowManager/WindowManagerContext";
import {
    eindeutigerVorgang,
    zieleAusKeys,
    type PickerZiel,
} from "../vorgang/picker/vorgangPickerListe";
import { VorgangMehrfachPicker } from "../vorgang/picker/VorgangMehrfachPicker";
import { reportMailError } from "../../services/mailError";
import { DokumentNameFeld, geaenderterDokumentName } from "../documents/DokumentNameFeld";
import { teileDateiname } from "../documents/DateinameKasten";

/**
 * „Bestehendem Vorgang zuordnen" — die Vorgangs-Auswahl aus einer Mail
 * (Cluster VORGANG, D1/D2/D3).
 *
 * Drei Dinge unterscheiden sie von der Vorfassung:
 *
 *  - **Rangfolge statt flacher Liste:** zuerst alle Vorgänge des erkannten
 *    Kontakts — auch die, an denen der Nutzer nicht beteiligt ist —, dann
 *    seine eigenen und die seines Teams, dann alle übrigen. Der Server liefert
 *    die Stufe je Zeile mit (`GET /vorgang/picker`) und filtert dabei erstmals
 *    auch auf Datensatzebene: ein fremder persönlicher Vorgang taucht hier nie
 *    auf, auch nicht über die Suche.
 *  - **To-dos sind mitdurchsuchbar (D3):** ein treffendes Todo erscheint als
 *    eingerückte Unterzeile seines Vorgangs und ist einzeln anhakbar. Damit
 *    entfällt das frühere „Mail zuweisen — To-Do wählen"-Overlay, das ALLE
 *    Todos des Mandanten lud und im Browser filterte.
 *  - **kvp-rd statt Fluent:** die Auswahl trägt dieselben Klassen wie der
 *    Zuordnungs-Dialog im Vorgang-Detail (`vgd-linkdialog-*`).
 *
 * T-34 (PO 01.10.2026): Unter einem aufgeklappten Vorgang MIT To-dos steht
 * „Neues To-do…" — ein Betreff genügt, das To-do entsteht beim „Zuordnen"
 * (Bezüge und Rollen vom Vorgang) und bekommt die Mail. Ein Vorgang OHNE
 * To-dos bleibt direktes Ziel; dort gibt es die Zeile nicht (Invariante
 * PO 22.09.2026: „nur an Vorgang zuweisen, wenn er keine To-dos hat").
 *
 * Datei- und Exportname bleiben absichtlich erhalten: Cluster POSTFACH (G2,
 * Rechtsklick → „Einem Vorgang zuweisen") importiert denselben Einstieg.
 *
 * Seit 01.10.2026 ist das hier nur noch die Hülle (Fenster, Mail-Fehlertexte,
 * `onAssign`); die Auswahl selbst ist `VorgangMehrfachPicker` — dieselbe, die
 * die Telefonie nutzt. Verhalten unverändert.
 */

export type MailVorgangZiel = PickerZiel;

export type OpenMailVorgangPickerParams = {
    /**
     * Erkannter Kontakt der Mail — Grundlage der Rangstufe 1. Ohne ihn beginnt
     * die Liste bei Stufe 2, ohne Fehlermeldung.
     */
    contactId?: number | null;
    /**
     * Zuweisen der ausgewählten Ziele (Vorgänge UND/ODER Todos). Der Aufrufer
     * schreibt die Links und archiviert die Mail — und zwar ERST NACHDEM alle
     * Links geschrieben sind (siehe `assignMailToZiele`).
     */
    onAssign: (
        ziele: MailVorgangZiel[],
        navigationVorgangId: number | null,
        /** T-397: neuer Name von `dokument` (inkl. Endung) — nur, wenn der Nutzer ihn geändert hat. */
        dokumentName?: string,
    ) => Promise<void>;
    /**
     * Betreff der Mail — T-34: per Knopf als Betreff eines neuen To-dos
     * übernehmbar. Vorbelegt wird nichts.
     */
    mailBetreff?: string | null;
    /**
     * T-397: das PDF eines DVAG-Kontakthistorie-Eintrags, das mit dem Zuordnen als
     * Dokument am Ziel landet. Die Auswahl zeigt seinen Namen zum Ändern und meldet
     * den neuen Namen an `onAssign`. Für Mails entfällt der Block.
     */
    dokument?: { name: string };
};

type Props = OpenMailVorgangPickerParams & { windowId: string };

export const MailVorgangPickerWindow: React.FC<Props> = ({ windowId, contactId, onAssign, mailBetreff, dokument }) => {
    const { closeWindow } = useWindowManager();
    const [dokumentStamm, setDokumentStamm] = React.useState(() => teileDateiname(dokument?.name ?? "").stamm);
    const fehlerText = React.useCallback(
        (error: unknown, art: "anlegen" | "zuordnen") =>
            reportMailError({
                area: "mail",
                action: "assign",
                error,
                fallbackMessage:
                    art === "anlegen" ? "To-do konnte nicht angelegt werden." : "Zuordnung fehlgeschlagen.",
            }),
        [],
    );
    return (
        <VorgangMehrfachPicker
            contactId={contactId}
            betreffVorschlag={mailBetreff}
            onAbbrechen={() => closeWindow(windowId)}
            fehlerText={fehlerText}
            zusatz={
                dokument && (
                    <DokumentNameFeld original={dokument.name} stamm={dokumentStamm} onStamm={setDokumentStamm} />
                )
            }
            onUebernehmen={async ({ auswahl }) => {
                const ziele = zieleAusKeys(auswahl.keys());
                if (ziele.length === 0) return;
                const vorgangId = eindeutigerVorgang(auswahl.values());
                const neuerName = dokument ? geaenderterDokumentName(dokument.name, dokumentStamm) : undefined;
                // Ohne neuen Namen bleibt der Aufruf wie vor T-397 (zwei Argumente).
                await (neuerName ? onAssign(ziele, vorgangId, neuerName) : onAssign(ziele, vorgangId));
                closeWindow(windowId);
            }}
        />
    );
};

/** Öffnet die Vorgangs-Auswahl als WindowManager-Fenster. */
export const openMailVorgangPicker = (
    openWindow: (config: OpenWindowConfig) => void,
    params: OpenMailVorgangPickerParams,
): string => {
    const windowId = `mail-vorgang-picker-${Date.now()}`;
    openWindow({
        id: windowId,
        title: "Bestehendem Vorgang zuordnen",
        icon: React.createElement(TaskListSquareLtr20Regular),
        initialSize: { width: 560, height: 600 },
        center: true,
        render: () => React.createElement(MailVorgangPickerWindow, { windowId, ...params }),
    });
    return windowId;
};

export default MailVorgangPickerWindow;
